import { captureException } from '../utils/sentry'
import { getItem, setItemOrThrow } from '../utils/storage'

const DEVELOPER_MODE_STORAGE_KEY = 'developer-mode-enabled'

let isEnabled = false
let hydrationPromise: Promise<void> | null = null
let togglePromise: Promise<boolean> | null = null
const listeners = new Set<() => void>()

function publish(nextValue: boolean) {
  if (isEnabled === nextValue) return

  isEnabled = nextValue
  listeners.forEach((listener) => listener())
}

export function getDeveloperModeSnapshot(): boolean {
  return isEnabled
}

export function subscribeToDeveloperMode(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function hydrateDeveloperMode(): Promise<void> {
  hydrationPromise ??= getItem(DEVELOPER_MODE_STORAGE_KEY, {
    scoped: false,
  })
    .then((storedValue) => publish(storedValue === 'true'))
    .catch((error: unknown) => {
      captureException(error, {
        feature: 'developer-mode',
        tags: { op: 'hydrate' },
      })
    })

  return hydrationPromise
}

async function persistToggledDeveloperMode(): Promise<boolean> {
  try {
    await hydrateDeveloperMode()

    const nextValue = !isEnabled
    await setItemOrThrow(DEVELOPER_MODE_STORAGE_KEY, String(nextValue), {
      scoped: false,
    })
    publish(nextValue)
    return nextValue
  } catch (error) {
    captureException(error, {
      feature: 'developer-mode',
      tags: { op: 'persist' },
    })
    throw error
  }
}

export function toggleDeveloperMode(): Promise<boolean> {
  togglePromise ??= persistToggledDeveloperMode().finally(() => {
    togglePromise = null
  })

  return togglePromise
}
